import React, { useEffect } from 'react';
import { Checkbox, Col, Divider, Form, Input, message, Row, Typography } from 'antd';
import FeatherIcon from 'feather-icons-react';
import { observer } from 'mobx-react';
import { UploadFile, RichTextEditor } from '@partsbadger/library';
import { QuestionMark } from '@partsbadger/ui-library';
import { QuoteProps, IRequirement, ICustomRequirements } from '@partsbadger/types';
import { AdditionalRequirementsSelector } from './AdditionalRequirementsSelector';

import {
    ShoppingCartOutlined as ShoppingCart,
    ClockCircleOutlined as Clock,
    InfoCircleOutlined as Info,
    MinusOutlined as Minus,
    PlusOutlined as Plus,
    FileTextOutlined,
    DownOutlined as ChevronDown,
    RightOutlined as ChevronRight,
    SearchOutlined as Search,
    CloseOutlined as X,
} from '@ant-design/icons';

import _ from 'lodash';

const { Text } = Typography;

const SYNC_REQUIREMENT_NAMES = {
    domestic: 'Domestic Production Required',
    create2d: 'Create 2D Files',
    create3d: 'Create 3D Files',
    customerSuppliedMaterial: 'Customer Supplied Material',
} as const;

interface Props {
    handleUpdateQuoteField: (field: string, value: unknown) => void;
    handleUpdateQuoteFields: (payload: Record<string, unknown>) => void;
    quote: QuoteProps;
    isRequote: boolean;
    additionalRequirements: IRequirement[];
    isStaffUser: boolean;
}

const buildRequirementsFromQuote = (
    quote: QuoteProps,
    additionalRequirements: IRequirement[]
): ICustomRequirements[] => {
    const existing = quote?.custom_requirements ?? [];
    const byKey = new Map<string, ICustomRequirements>(existing.map(req => [String(req.key), req]));

    const findRequirementByName = (name: string) =>
        additionalRequirements.find(req => req.name?.trim().toLowerCase() === name.trim().toLowerCase());

    const syncMap = [
        {
            enabled: !!quote?.domestic_quoting,
            requirement: findRequirementByName(SYNC_REQUIREMENT_NAMES.domestic),
        },
        {
            enabled: !!quote?.create_2d_files,
            requirement: findRequirementByName(SYNC_REQUIREMENT_NAMES.create2d),
        },
        {
            enabled: !!quote?.create_3d_files,
            requirement: findRequirementByName(SYNC_REQUIREMENT_NAMES.create3d),
        },
        {
            enabled: !!quote?.customer_supplied_material,
            requirement: findRequirementByName(SYNC_REQUIREMENT_NAMES.customerSuppliedMaterial),
        },
    ];

    for (const entry of syncMap) {
        if (!entry.enabled || !entry.requirement?.id) continue;

        const key = String(entry.requirement.id);
        if (!byKey.has(key)) {
            byKey.set(key, {
                key,
                label: entry.requirement.name,
                value: '',
                files: [],
            });
        }
    }

    return Array.from(byKey.values());
};

const getRequirementsStorageKey = (quote?: QuoteProps) => `quote-additional-requirements:${quote?.id ?? 'draft'}`;

const parseStoredRequirements = (raw: string | null): ICustomRequirements[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const DeliveryAndRequirementsSection: React.FC<Props> = observer(
    ({ handleUpdateQuoteField, handleUpdateQuoteFields, quote, isRequote, additionalRequirements, isStaffUser }) => {
        const form = Form.useFormInstance();

        const pendingQuoteFieldsRef = React.useRef<Record<string, unknown>>({});

        const flushPendingQuoteFields = React.useCallback(() => {
            const pending = pendingQuoteFieldsRef.current;
            if (Object.keys(pending).length === 0) return;
            pendingQuoteFieldsRef.current = {};
            handleUpdateQuoteFields(pending);
        }, [handleUpdateQuoteFields]);

        const debouncedFlushPendingQuoteFields = React.useMemo(() => _.debounce(flushPendingQuoteFields, 1000), [
            flushPendingQuoteFields,
        ]);

        const queueQuoteFields = React.useCallback(
            (payload: Record<string, unknown>) => {
                // lodash debounce keeps only the latest call arguments, so merge
                // into a ref to make the trailing PATCH lossless across rapid toggles.
                Object.assign(pendingQuoteFieldsRef.current, payload);
                debouncedFlushPendingQuoteFields();
            },
            [debouncedFlushPendingQuoteFields]
        );

        const persistRequirementsAndFlags = (values: ICustomRequirements[]) => {
            const selectedIds = new Set(values.map(item => Number(item.key)));

            const payload: Record<string, unknown> = {
                custom_requirements: values,
            };

            if (domesticRequirement?.id) {
                payload.domestic_quoting = selectedIds.has(Number(domesticRequirement.id));
            }

            if (create2dRequirement?.id) {
                payload.create_2d_files = selectedIds.has(Number(create2dRequirement.id));
            }

            if (create3dRequirement?.id) {
                payload.create_3d_files = selectedIds.has(Number(create3dRequirement.id));
            }

            if (customerSuppliedMaterialRequirement?.id) {
                payload.customer_supplied_material = selectedIds.has(Number(customerSuppliedMaterialRequirement.id));
            }

            queueQuoteFields(payload);
        };

        const handleUpdateRequirements = (values: ICustomRequirements[]) => {
            setLocalRequirements(values);
            form.setFieldsValue({ custom_requirements_local: values });
            persistRequirementsAndFlags(values);
            sessionStorage.setItem(storageKey, JSON.stringify(values));
        };

        const findRequirementByName = (name: string) =>
            additionalRequirements.find(req => req.name?.trim().toLowerCase() === name.trim().toLowerCase());

        const domesticRequirement = findRequirementByName(SYNC_REQUIREMENT_NAMES.domestic);
        const create2dRequirement = findRequirementByName(SYNC_REQUIREMENT_NAMES.create2d);
        const create3dRequirement = findRequirementByName(SYNC_REQUIREMENT_NAMES.create3d);
        const customerSuppliedMaterialRequirement = findRequirementByName(
            SYNC_REQUIREMENT_NAMES.customerSuppliedMaterial
        );

        const derivedRequirements = React.useMemo(() => buildRequirementsFromQuote(quote, additionalRequirements), [
            quote,
            additionalRequirements,
        ]);

        const storageKey = React.useMemo(() => getRequirementsStorageKey(quote), [quote?.id]);

        const [localRequirements, setLocalRequirements] = React.useState<ICustomRequirements[]>(() => {
            const stored = parseStoredRequirements(sessionStorage.getItem(getRequirementsStorageKey(quote)));
            return stored.length > 0 ? stored : derivedRequirements;
        });

        useEffect(() => {
            sessionStorage.setItem(storageKey, JSON.stringify(localRequirements));
            form.setFieldsValue({ custom_requirements_local: localRequirements });
        }, [storageKey, localRequirements, form]);

        useEffect(() => {
            const stored = parseStoredRequirements(sessionStorage.getItem(storageKey));
            const next = stored.length > 0 ? stored : derivedRequirements;

            setLocalRequirements(next);
            form.setFieldsValue({ custom_requirements_local: next });
        }, [storageKey, derivedRequirements, form]);

        const selectedRequirementIds = new Set(localRequirements.map(item => Number(item.key)));

        const isRequirementSelected = (requirement?: IRequirement) => {
            if (!requirement?.id) return false;
            return selectedRequirementIds.has(Number(requirement.id));
        };

        const domesticChecked = isRequirementSelected(domesticRequirement);
        const create2dChecked = isRequirementSelected(create2dRequirement);
        const create3dChecked = isRequirementSelected(create3dRequirement);
        const customerSuppliedMaterialChecked = isRequirementSelected(customerSuppliedMaterialRequirement);

        const syncCheckboxAndRequirement = (
            field: 'customer_supplied_material' | 'domestic_quoting' | 'create_2d_files' | 'create_3d_files',
            checked: boolean,
            requirement?: IRequirement
        ) => {
            const currentRequirements: ICustomRequirements[] = localRequirements;
            let nextRequirements: ICustomRequirements[] = currentRequirements;

            if (requirement?.id != null) {
                const requirementId = Number(requirement.id);
                const exists = currentRequirements.some(item => Number(item.key) === requirementId);

                if (checked && !exists) {
                    nextRequirements = [
                        ...currentRequirements,
                        {
                            key: String(requirementId),
                            label: requirement.name,
                            value: '',
                            files: [],
                        },
                    ];
                } else if (!checked && exists) {
                    nextRequirements = currentRequirements.filter(item => Number(item.key) !== requirementId);
                }
            }

            setLocalRequirements(nextRequirements);
            form.setFieldsValue({ custom_requirements_local: nextRequirements });
            sessionStorage.setItem(storageKey, JSON.stringify(nextRequirements));
            queueQuoteFields({
                [field]: checked,
                custom_requirements: nextRequirements,
            });
        };

        const renderNotes = (!quote?.contact?.is_hobbyist || isRequote) && (
            <Col md={24} span={24} data-testid="additional-notes-section">
                <Form.Item
                    name="other_notes"
                    initialValue={quote ? quote.customer_statement : ''}
                    label="Additional Notes"
                    className="mb-0"
                    // help={!isRequote && 'Copy and paste the customer statement'}
                >
                    <RichTextEditor
                        placeholder="Adding any notes will require a manual review by a Design Engineer."
                        className="richtexteditor"
                        onChange={html => {
                            queueQuoteFields({ customer_statement: html });
                        }}
                    />
                </Form.Item>
            </Col>
        );

        const needs2DFiles = () => {
            return (
                quote &&
                quote?.line_items?.filter(item => {
                    return !item.product.has_file_2d;
                })?.length > 0
            );
        };

        const needs3DFiles = () => {
            return (
                quote &&
                quote.line_items?.filter(item => {
                    return !item.product.has_file_3d;
                })?.length > 0
            );
        };

        useEffect(() => {
            form.setFieldsValue({
                customer_supplied_material: customerSuppliedMaterialChecked,
                domestic_quoting: domesticChecked,
                create_2d_files: create2dChecked,
                create_3d_files: create3dChecked,
            });
        }, [form, customerSuppliedMaterialChecked, domesticChecked, create2dChecked, create3dChecked]);

        useEffect(() => {
            return () => {
                debouncedFlushPendingQuoteFields.cancel();
                flushPendingQuoteFields();
            };
        }, [debouncedFlushPendingQuoteFields, flushPendingQuoteFields]);

        return (
            <div data-testid="additional-requirements-section">
                <div className="space-y-3">
                    <h4 className="font-medium text-slate-900 flex items-center space-x-2">
                        <FileTextOutlined className="w-4 h-4" />
                        <span>Additional Requirements</span>
                    </h4>
                    <Col xs={24}>
                        <Typography.Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                            Some of the options below may affect the price and lead time, and may require review by an
                            engineer.
                        </Typography.Text>
                    </Col>

                    <Col md={24} span={24}>
                        <AdditionalRequirementsSelector
                            requirements={additionalRequirements}
                            title="Additional Requirements from my Account"
                            defaultSelectedIds={localRequirements.map(item => Number(item.key))}
                            customRequirements={localRequirements}
                            onChange={handleUpdateRequirements}
                            expandSubLabel="ISO, shipping, certificates…"
                        />
                    </Col>
                </div>

                <Col md={24} span={24}>
                    <Row gutter={[12, 0]}>
                        <>
                            <Col md={12} span={12}>
                                <Form.Item
                                    name="customer_supplied_material"
                                    initialValue={quote?.customer_supplied_material}
                                    valuePropName="checked"
                                    className="mb-0"
                                >
                                    <Checkbox
                                        checked={customerSuppliedMaterialChecked}
                                        onChange={e =>
                                            syncCheckboxAndRequirement(
                                                'customer_supplied_material',
                                                e.target.checked,
                                                customerSuppliedMaterialRequirement
                                            )
                                        }
                                    >
                                        Customer Supplied Material
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col md={12} span={12}>
                                <Form.Item
                                    name="domestic_quoting"
                                    initialValue={quote?.domestic_quoting}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        checked={domesticChecked}
                                        onChange={e =>
                                            syncCheckboxAndRequirement(
                                                'domestic_quoting',
                                                e.target.checked,
                                                domesticRequirement
                                            )
                                        }
                                    >
                                        Domestic Production Required
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            {needs2DFiles() && (
                                <Col md={12} span={12}>
                                    <Form.Item
                                        name="create_2d_files"
                                        valuePropName="checked"
                                        initialValue={quote?.create_2d_files}
                                    >
                                        <Checkbox
                                            checked={create2dChecked}
                                            onChange={e =>
                                                syncCheckboxAndRequirement(
                                                    'create_2d_files',
                                                    e.target.checked,
                                                    create2dRequirement
                                                )
                                            }
                                        >
                                            <Text strong>Create 2D Files</Text>
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                            )}
                            {needs3DFiles() && (
                                <Col md={12} xs={12}>
                                    <Form.Item
                                        name="create_3d_files"
                                        valuePropName="checked"
                                        initialValue={quote?.create_3d_files}
                                    >
                                        <Checkbox
                                            checked={create3dChecked}
                                            onChange={e =>
                                                syncCheckboxAndRequirement(
                                                    'create_3d_files',
                                                    e.target.checked,
                                                    create3dRequirement
                                                )
                                            }
                                        >
                                            <Text strong>Create 3D Files</Text>
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                            )}
                            {/* {(needs3DFiles() || needs2DFiles()) && <Divider />} */}

                            <Col md={24} span={24} data-testid="additional-files-section">
                                <div data-testid="additional-files-upload">
                                    <Form.Item
                                        name="additional_files"
                                        label={
                                            <span>
                                                Additional Files
                                                <QuestionMark>
                                                    Uploading additional files will require a manual review by a Design
                                                    Engineer.
                                                </QuestionMark>
                                            </span>
                                        }
                                    >
                                        <UploadFile
                                            accept={['png', 'png', 'jpg', 'pdf']}
                                            renderAs="dragger"
                                            multiple
                                            onUpload={info => {
                                                const status = info.status;
                                                const additionalFiles = form
                                                    .getFieldValue('additional_files')
                                                    ?.map((f: { response: { id: number } }) => f.response?.id)
                                                    .filter((id: number) => id !== undefined && id !== null);

                                                if (status === 'done') {
                                                    if (additionalFiles) {
                                                        queueQuoteFields({ additional_files: additionalFiles });
                                                    }
                                                    message.success(`${info.name} file uploaded successfully.`);
                                                } else if (status === 'error') {
                                                    message.error(`${info.name} file upload failed.`);
                                                } else if (status === 'removed') {
                                                    queueQuoteFields({ additional_files: additionalFiles ?? [] });
                                                }
                                            }}
                                            showUploadList={true}
                                            onRemove={info => {
                                                const filteredFiles = form
                                                    .getFieldValue('additional_files')
                                                    ?.filter((item: { id: number }) => item.id !== info.response?.id);

                                                const additionalFiles = filteredFiles
                                                    ?.map((f: { response: { id: number } }) => f.response?.id)
                                                    .filter((id: number) => id !== undefined && id !== null);
                                                queueQuoteFields({ additional_files: additionalFiles ?? [] });
                                            }}
                                            fileRowTestIdPrefix="additional-files-file-row"
                                            fileRemoveTestIdPrefix="additional-files-file-remove"
                                            data-testid="upload-file"
                                        >
                                            <div className="d-flex align-items-center align-middle justify-content-center mb-2 text-lg">
                                                <FeatherIcon icon="upload-cloud" />
                                                <span className="ml-2">Click or drag files</span>
                                            </div>
                                            <p>Add any additional files like NDAs, Tax Exempt Forms, Photos, etc.</p>
                                        </UploadFile>
                                    </Form.Item>
                                </div>
                            </Col>

                            {renderNotes}
                        </>
                    </Row>
                </Col>
            </div>
        );
    }
);
